import type { ReactElement } from "react";
import { Alert, Button, Chip, Overlay } from "../../ui";
import { useBreakpoint } from "../../lib/useBreakpoint";
import { CredentialRow } from "./CredentialRow";
import { RecordAutocomplete } from "./RecordAutocomplete";
import { SENDER } from "./api";
import { useGenerateLead } from "./GenerateLeadDialog.hook";
import type { LeadChannel, LeadEntityType, Sandbox } from "./types";
import styles from "./GenerateLeadDialog.module.css";

const CHANNEL_LABEL: Record<LeadChannel, string> = {
  call: "Call",
  whatsapp: "WhatsApp",
  email: "Email",
};

const TARGET_COPY: Record<
  LeadEntityType,
  { type: string; label: string; placeholder: string }
> = {
  listing: {
    type: "Listing",
    label: "Listing",
    placeholder: "Search seeded listings",
  },
  agent: { type: "Agent", label: "Agent", placeholder: "Search seeded agents" },
};

interface GenerateLeadDialogProps {
  sandbox: Sandbox;
  open: boolean;
  onClose: () => void;
}

export function GenerateLeadDialog({
  sandbox,
  open,
  onClose,
}: GenerateLeadDialogProps): ReactElement {
  const { isMobile } = useBreakpoint();
  const lead = useGenerateLead(sandbox.id, onClose);

  return (
    <Overlay
      open={open}
      onClose={lead.onClose}
      placement={isMobile ? "sheet-bottom" : "center"}
      label="Generate a test lead"
      panelStyle={
        isMobile
          ? { overflow: "visible" }
          : {
              overflow: "visible",
              inlineSize: "min(92vw, 480px)",
              translate: "calc(var(--nav-w) / 2) 0",
            }
      }
    >
      <div className={styles.dialog}>
        <header className={styles.header}>
          <h2 className={styles.title}>
            {lead.latest ? "Lead generated" : "Generate a test lead"}
          </h2>
          <p className={styles.subtitle}>{sandbox.name}</p>
        </header>

        {lead.latest ? (
          <div className={styles.result}>
            <Alert tone="info">
              Leads are not listed in PFDN. Keep this identifier — it is how you
              match the lead your integration receives.
            </Alert>
            <CredentialRow label="publicId" value={lead.latest.publicId} />
            <CredentialRow label="id" value={lead.latest.id} />
            {lead.leads.length > 1 ? (
              <p className={styles.earlier}>
                {lead.leads.length - 1} earlier lead
                {lead.leads.length > 2 ? "s" : ""} this session
              </p>
            ) : null}
          </div>
        ) : (
          <div className={styles.form}>
            <div className={styles.group}>
              <span className={styles.label}>Lead type</span>
              <div className={styles.chips}>
                {(Object.keys(TARGET_COPY) as LeadEntityType[]).map((key) => (
                  <Chip
                    key={key}
                    tone="brand"
                    selected={lead.entityType === key}
                    onClick={() => lead.setEntityType(key)}
                  >
                    {TARGET_COPY[key].type}
                  </Chip>
                ))}
              </div>
            </div>

            <RecordAutocomplete
              label={TARGET_COPY[lead.entityType].label}
              placeholder={TARGET_COPY[lead.entityType].placeholder}
              selected={lead.target}
              search={lead.search}
              onSelect={lead.setTarget}
              onClear={() => lead.setTarget(null)}
            />

            <div className={styles.group}>
              <span className={styles.label}>Channel</span>
              <div className={styles.chips}>
                {lead.channels.map((option) => (
                  <Chip
                    key={option}
                    tone="brand"
                    selected={lead.channel === option}
                    onClick={() => lead.setChannel(option)}
                  >
                    {CHANNEL_LABEL[option]}
                  </Chip>
                ))}
              </div>
            </div>

            <div className={styles.group}>
              <span className={styles.label}>Sender information</span>
              <dl className={styles.sender}>
                <dt>Name</dt>
                <dd>{SENDER.name}</dd>
                <dt>Email</dt>
                <dd>{SENDER.email}</dd>
                <dt>Phone</dt>
                <dd>{SENDER.phone}</dd>
              </dl>
              <p className={styles.senderNote}>
                Lead delivery is skipped in sandbox mode. The agent will not
                receive the actual message.
              </p>
            </div>

            {lead.error ? <Alert tone="danger">{lead.error}</Alert> : null}
          </div>
        )}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={lead.onClose}>
            {lead.latest ? "Done" : "Cancel"}
          </Button>
          <Button
            disabled={!lead.latest && !lead.target}
            loading={lead.busy}
            onClick={lead.latest ? lead.onGenerateAnother : lead.onGenerate}
          >
            {lead.latest ? "Generate another" : "Generate lead"}
          </Button>
        </div>
      </div>
    </Overlay>
  );
}
